import React, { useMemo, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { lensTokens } from '~/configs/unifiedAnalyticsTheme';
import { type NativePrintProps, NativeReportShell } from '~/nativeLenses/NativePrintBoard';
import { nativeLensId } from '~/nativeLenses/nativeLensId';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';

import { useGetReportingLeLensData } from '~/hooks/apis/reporting/useGetReportingLeLensData';

import type ApiError from '~/types/ApiError';

import AgentHubBdmChart from './AgentHubBdmChart';
import {
  AGENT_HUB_BDM_PAGE_ID,
  ATTRIBUTION_WINDOWS,
  type BdmFilters,
  CADENCES,
  COMPARISONS,
  COVERAGE_SEGMENTS,
  type CardProblem,
  type CardResult,
  type ChartData,
  type Kpi,
  SPLITS,
  agencyStatusData,
  changeText,
  chartCsv,
  cohortData,
  comparisonRange,
  conversionData,
  defaultFilters,
  effortData,
  filtersError,
  formatValue,
  isProblem,
  kpisOf,
  lensFilters,
  newVsExistingData,
  parseFilters,
  readCard,
  text,
  writeFilters,
} from './model';
import { type PageChart, agentHubBdmPageView, editFromBridge } from './pageAdapter';

const LENS_ID = nativeLensId(AGENT_HUB_BDM_PAGE_ID);
const ACTIVITY_SOURCE = 'analytics.agent_hub_interactions_bookings';
const COVERAGE_SOURCE = 'analytics.agent_hub_agency_coverage';

type ChartPanel = {
  key: string;
  title: string;
  results: Array<CardResult | CardProblem>;
  data: (results: Array<CardResult>) => ChartData;
  caption: string;
  height?: number;
};

function download(name: string, contents: string) {
  const url = URL.createObjectURL(new Blob(['﻿', contents], { type: 'text/csv;charset=utf-8;' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}

const kpiFormat = (kpi: Kpi) =>
  kpi.key === 'converted' || kpi.key === 'newAgentShare'
    ? 'percent'
    : kpi.key === 'bookingsPer100'
      ? 'decimal'
      : 'number';

function AgentHubBdmContent() {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const location = useLocation();
  const navigate = useNavigate();
  const [methodOpen, setMethodOpen] = useState(false);
  const filters = useMemo(() => parseFilters(new URLSearchParams(location.search)), [location.search]);
  const update = (change: Partial<BdmFilters>) =>
    navigate({
      pathname: location.pathname,
      search: writeFilters({ ...filters, ...change }, new URLSearchParams(location.search)).toString(),
      hash: location.hash,
    });
  const error = filtersError(filters);
  const request = useMemo(() => lensFilters(filters), [filters]);
  const query = useGetReportingLeLensData(error ? undefined : LENS_ID, request);
  const cards = query.data?.cards ?? [];
  // Filter options only list BDMs active in the KPI window, so the full-history Effort vs Output rows
  // add the rest. Every name seen stays listed while later loads run.
  const seenBdms = useRef(new Set<string>());
  const effortRows = readCard(cards, 'effort_by_bdm');
  [
    ...(query.data?.filterOptions?.bdm_name ?? []),
    ...(isProblem(effortRows) ? [] : effortRows.rows.map((row) => text(row.bdm_name))),
  ].forEach((name) => seenBdms.current.add(name));
  const bdmOptions = [...seenBdms.current].sort((a, b) => a.localeCompare(b));
  const ready = !error && query.isSuccess && !query.isFetching && !query.isShowingPreviousData;
  const failure = query.error as ApiError | null;
  const card = (id: string) => readCard(cards, id);
  const compare = comparisonRange(filters);
  const period = `${filters.from} to ${filters.to}`;
  const comparisonPeriod = `${compare.from} to ${compare.to}`;

  const totals = card('kpi_totals');
  const agencies = card('kpi_agencies');
  const kpis = isProblem(totals) || isProblem(agencies) ? null : kpisOf(totals, agencies, filters.attributionWindow);
  const trendCaption = (source: string, from: string) =>
    `${source} · ${filters.cadence} periods from ${from} · not limited by the KPI dates`;

  const effort: ChartPanel = {
    key: 'effort',
    title: 'Effort vs Output',
    results: [card(filters.split === 'BDM Name' ? 'effort_by_bdm' : 'effort_by_booked')],
    data: ([result]) => effortData(result, filters.split),
    caption: `${trendCaption(ACTIVITY_SOURCE, '1 Sep 2025')} · split by ${filters.split}`,
    height: 380,
  };
  const middle: Array<ChartPanel> = [
    {
      key: 'conversion',
      title: 'Conversion Efficiency',
      results: [card('conversion_totals'), card('conversion_agencies')],
      data: ([t, a]) => conversionData(t, a, filters.attributionWindow),
      caption: trendCaption(ACTIVITY_SOURCE, '1 Sep 2025'),
    },
    {
      key: 'signup',
      title: 'Sign-Up Performance',
      results: [card('signup_performance')],
      data: ([result]) => cohortData(result, 'signup_period'),
      caption: `${COVERAGE_SOURCE} latest day · agencies signed up from 1 Sep 2025, by signup ${filters.cadence.toLowerCase()} period and booking count`,
    },
    {
      key: 'first-booking',
      title: 'First-Booking Performance',
      results: [card('first_booking_performance')],
      data: ([result]) => cohortData(result, 'first_booking_period'),
      caption: `${COVERAGE_SOURCE} latest day · agencies first booked from 1 Sep 2025, by first booking ${filters.cadence.toLowerCase()} period`,
    },
  ];
  const bottom: Array<ChartPanel> = [
    {
      key: 'new-vs-existing',
      title: 'New vs Existing Bookings',
      results: [card('new_vs_existing')],
      data: ([result]) => newVsExistingData(result),
      caption: `${trendCaption(ACTIVITY_SOURCE, '1 Sep 2025')} · percent of each period's agency bookings`,
    },
    {
      key: 'agency-status',
      title: 'Agency Status',
      results: [card('agency_status')],
      data: ([result]) => agencyStatusData(result),
      caption: `${trendCaption(COVERAGE_SOURCE, '1 Jul 2025')} · distinct agencies by coverage segment`,
    },
  ];
  const panels = [effort, ...middle, ...bottom];
  const chartData = (panel: ChartPanel) => {
    const problem = panel.results.find(isProblem);
    return problem ? null : panel.data(panel.results as Array<CardResult>);
  };
  const truncated = (panel: ChartPanel) => panel.results.some((result) => !isProblem(result) && result.truncated);
  const pageCharts: Array<PageChart> = panels.map((panel) => ({
    title: panel.title,
    data: chartData(panel),
    truncated: truncated(panel),
  }));

  const pageView = agentHubBdmPageView({
    ready,
    filters,
    bdmOptions,
    period,
    comparisonPeriod,
    kpis,
    charts: pageCharts,
  });
  usePageBridge({
    pageId: AGENT_HUB_BDM_PAGE_ID,
    view: pageView,
    edit: (changes) => {
      if (!ready) {
        throw new Error('Page data is still loading.');
      }
      update(editFromBridge(changes, pageView));
    },
  });

  const renderPanel = (panel: ChartPanel, index: number) => {
    const problem = panel.results.find(isProblem);
    if (problem) {
      return (
        <Alert key={panel.key} severity={query.isFetching ? 'info' : 'error'}>
          {query.isFetching ? 'Loading…' : problem.problem}
        </Alert>
      );
    }
    const { data } = pageCharts[index];
    return (
      <AgentHubBdmChart
        key={panel.key}
        title={panel.title}
        data={data}
        cadence={filters.cadence}
        caption={panel.caption}
        height={panel.height}
        truncated={pageCharts[index].truncated}
        onCsv={() =>
          download(
            `agent-hub-bdm-${panel.key}-${filters.cadence.toLowerCase()}.csv`,
            chartCsv(panel.title, data, filters.cadence),
          )
        }
      />
    );
  };

  const select = <T extends string | number>(
    label: string,
    value: T,
    values: ReadonlyArray<T>,
    onChange: (value: T) => void,
    render: (value: T) => string = String,
  ) => (
    <TextField
      select
      size="small"
      label={label}
      value={String(value)}
      onChange={(event) => onChange(values.find((option) => String(option) === event.target.value) ?? value)}
      fullWidth
    >
      {values.map((option) => (
        <MenuItem key={String(option)} value={String(option)}>
          {render(option)}
        </MenuItem>
      ))}
    </TextField>
  );

  const filterPanel = (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: tokens.surface }}>
      <Stack gap={1.5}>
        <Typography variant="subtitle2">Filters</Typography>
        <Stack direction="row" gap={1}>
          <TextField
            size="small"
            type="date"
            label="Start date"
            value={filters.from}
            onChange={(event) => update({ from: event.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            size="small"
            type="date"
            label="End date"
            value={filters.to}
            onChange={(event) => update({ to: event.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Stack>
        {select('Comparison period', filters.comparison, COMPARISONS, (comparison) => update({ comparison }))}
        {select('Date cadence', filters.cadence, CADENCES, (cadence) => update({ cadence }))}
        {select(
          'Attribution window',
          filters.attributionWindow,
          ATTRIBUTION_WINDOWS,
          (attributionWindow) => update({ attributionWindow }),
          (days) => `${days} days`,
        )}
        {select('Effort vs Output split', filters.split, SPLITS, (split) => update({ split }))}
        <Autocomplete
          multiple
          size="small"
          options={bdmOptions}
          value={filters.bdm}
          onChange={(_, bdm) => update({ bdm })}
          renderInput={(params) => <TextField {...params} label="BDM name" placeholder="All" />}
        />
        <Autocomplete
          multiple
          size="small"
          options={COVERAGE_SEGMENTS}
          value={filters.segments}
          onChange={(_, segments) => update({ segments })}
          renderInput={(params) => <TextField {...params} label="Coverage segment" placeholder="All" />}
        />
        <Button size="small" onClick={() => update(defaultFilters())}>
          Reset filters
        </Button>
      </Stack>
    </Paper>
  );

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, md: 3 }, minWidth: 0 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          gap={1}
        >
          <Box>
            <Typography component="h1" variant="h4" sx={{ fontSize: { xs: 25, sm: 30 }, color: tokens.accent }}>
              Agent Hub BDM Performance
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {period} vs {comparisonPeriod} ({filters.comparison}) · {filters.cadence} cadence · Sydney dates
            </Typography>
          </Box>
          <Stack direction="row" gap={1} alignItems="center">
            <Chip size="small" variant="outlined" color="primary" label={query.isFetching ? 'Updating…' : 'Live'} />
            <Button onClick={() => setMethodOpen(true)} size="small">
              How to read this
            </Button>
            <PageChatButton />
          </Stack>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 280px' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <Stack gap={2} sx={{ minWidth: 0, order: { xs: 2, lg: 1 } }}>
            {error && <Alert severity="error">{error}</Alert>}
            {failure && (
              <Alert
                severity={failure.status === 404 || failure.status === 403 ? 'warning' : 'error'}
                action={
                  <Button color="inherit" onClick={() => void query.refetch()}>
                    Retry
                  </Button>
                }
              >
                {failure.status === 404
                  ? 'Agent Hub BDM Performance is not registered in Library for this environment yet. A reporting admin registers it under Playground → Lens pages → Register native pages.'
                  : failure.status === 403
                    ? 'You do not have access to Agent Hub BDM Performance. Ask a reporting admin to add you to its Library folder.'
                    : failure.message || 'Agent Hub BDM Performance could not load.'}
              </Alert>
            )}
            {!error && !failure && query.isPending && (
              <Stack gap={2}>
                <Skeleton variant="rounded" height={100} />
                <Skeleton variant="rounded" height={380} />
              </Stack>
            )}
            {!error && query.data && (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' },
                    gap: 1.5,
                  }}
                >
                  {kpis
                    ? kpis.map((kpi) => (
                        <Paper key={kpi.key} variant="outlined" sx={{ p: 1.5, minWidth: 0, bgcolor: tokens.surface }}>
                          <Typography variant="caption" color="text.secondary">
                            {kpi.label}
                            {kpi.key === 'converted' ? ` (${filters.attributionWindow} days)` : ''}
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: { xs: 20, sm: 24 },
                              fontVariantNumeric: 'tabular-nums',
                              color: tokens.accent,
                            }}
                          >
                            {formatValue(kpi.current, kpiFormat(kpi))}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color:
                                kpi.change === null || kpi.change === 0
                                  ? 'text.secondary'
                                  : kpi.change > 0
                                    ? 'success.main'
                                    : 'error.main',
                            }}
                          >
                            {changeText(kpi.change)} · prev {formatValue(kpi.previous, kpiFormat(kpi))}
                          </Typography>
                        </Paper>
                      ))
                    : [totals, agencies].filter(isProblem).map((problem) => (
                        <Alert
                          key={problem.problem}
                          severity={query.isFetching ? 'info' : 'error'}
                          sx={{ gridColumn: '1 / -1' }}
                        >
                          {query.isFetching ? 'Loading…' : problem.problem}
                        </Alert>
                      ))}
                </Box>
                {renderPanel(effort, 0)}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                    gap: 2,
                  }}
                >
                  {middle.map((panel, index) => renderPanel(panel, 1 + index))}
                </Box>
                <Box
                  sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}
                >
                  {bottom.map((panel, index) => renderPanel(panel, 1 + middle.length + index))}
                </Box>
              </>
            )}
          </Stack>
          <Box sx={{ order: { xs: 1, lg: 2 }, position: { lg: 'sticky' }, top: { lg: 16 } }}>{filterPanel}</Box>
        </Box>
      </Stack>
      <Dialog open={methodOpen} onClose={() => setMethodOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>How to read this</DialogTitle>
        <DialogContent>
          <Stack gap={1.5}>
            {Object.entries(pageView.definitions as Record<string, string>).map(([key, value]) => (
              <Typography key={key} variant="body2">
                {value}
              </Typography>
            ))}
            <Typography variant="body2">
              Interactions are Salesforce tasks a BDM logged against an agency. Bookings are completed or pending Agent
              Hub bookings. Coverage segments: signed up in the period; active (booked in the last 12 months) and
              interacted; active and not interacted; inactive.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMethodOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default function AgentHubBdmPage({ printTarget }: NativePrintProps) {
  return (
    <NativeReportShell
      pageId="agent-hub-bdm"
      title="Agent Hub BDM Performance"
      printTarget={printTarget}
    >
      <AgentHubBdmContent />
    </NativeReportShell>
  );
}
